import { Calendar } from "@components/atoms/Calendar";
import { CanAccess, HttpError, useDelete, useGo, useTranslation } from "@refinedev/core";
import { UseModalFormReturnType } from "@refinedev/react-hook-form";
import { logger } from "@utils/logger";
import { minuteInterval, roundTime } from "@utils/time";
import dayjs from "dayjs";
import { calendar_v3 } from "googleapis";
import { useAppCalendar } from "hooks/calendar/useAppCalendar";
import { HTMLAttributes, useEffect, useMemo, useState } from "react";
import { FaLink, FaTrash } from "react-icons/fa6";

type ModalReturn = UseModalFormReturnType<calendar_v3.Schema$Event, HttpError, calendar_v3.Schema$Event, {}, calendar_v3.Schema$Event, calendar_v3.Schema$Event, HttpError>

export interface CalendarCreateProps extends HTMLAttributes<HTMLDivElement> {
    form: ModalReturn,
    onClose: () => void
}

export const CalendarEditCreate = ({ form, onClose, className, ...rest }: CalendarCreateProps) => {
    const { translate } = useTranslation();
    const go = useGo();
    const { mutate: deleteEvent } = useDelete({})
    const calendarId = useAppCalendar()

    const {
        formState: {
            errors,
            isSubmitSuccessful,
        },
        refineCore: { onFinish, formLoading, queryResult },
        modal: { close },
        register,
        reset,
        handleSubmit,
    } = form
    // logger.debug(queryResult, defaultValues)
    // logger.debug(queryResult?.data?.data?.summary)
    // logger.debug(form)

    const onFinishHandler = async (data: any) => {
        logger.debug(data)
        onFinish(data);
        close()
    };

    const onDeleteHandler = () => {
        deleteEvent({
            dataProviderName: 'gapi',
            resource: `calendar/calendars/:calendar/events`,
            id: form.refineCore.id!,
            meta: {
                params: {
                    calendar: calendarId?.availability
                }
            }
        })
        close()
    }

    useEffect(() => {
        reset()
    }, [isSubmitSuccessful])

    const timeInterval = useMemo(() => minuteInterval(dayjs().hour(0).minute(0), dayjs().hour(23).minute(59)), [])

    const DateTimeComponent = ({ form: { setValue, watch, register }, field, disableTime }: { form: ModalReturn, field: string, disableTime?: boolean }) => {
        const _field: any = disableTime ? `${field}.date` : `${field}.dateTime`
        const onChange = (e: any) => setValue(_field, e)
        const value = watch(_field)

        // logger.debug(_field, value)
        const [hour, setHour] = useState(dayjs(value).format("HH:mm"))
        const [date, setDate] = useState(dayjs(value).format("YYYY-MM-DD"))

        useEffect(() => {
            register(_field)
        }, [register])

        useEffect(() => {
            setHour(dayjs(value).format("HH:mm"))
            setDate(dayjs(value).format("YYYY-MM-DD"))
        }, [value])

        useEffect(() => {
            const newDate = disableTime ? dayjs(date).toISOString() : dayjs(`${date} ${hour}`).toISOString()
            // logger.debug(dayjs(`${date} ${hour}`).toISOString())
            if (newDate != value)
                onChange(newDate)
        }, [date, hour])

        return <>
            <label className="input input-bordered flex items-center gap-2 flex-1">
                <div className="dropdown">
                    <input onChange={(e) => setDate(e.target.value)} value={date} tabIndex={0} type="text" className="grow" />
                    <div className="dropdown-content card card-sm z-1 bg-base-100 shadow-lg">
                        <Calendar onDateChange={(e) => setDate(e)} selectedDate={date} tabIndex={0} className="card-body w-72" />
                    </div>
                </div>
            </label>
            {disableTime ? <></> : <select onChange={(e) => setHour(e.target.value)} value={hour} className="select select-bordered">
                {timeInterval.map((e, i) => <option key={i} value={dayjs(e).format("HH:mm")}>{dayjs(e).format("HH:mm")}</option>)}
            </select>}
        </>
    }

    const recurringEventId = queryResult?.data?.data?.recurringEventId
    const allDay = !!queryResult?.data?.data?.start?.date


    const RecurrenceComponent = ({ form: { setValue, watch, register }, field, disabled, ...rest }: { form: ModalReturn, field: string, disabled?: boolean }) => {
        const newField: any = field
        const onChange = (e: any) => {
            // logger.debug(e.target.value)
            setValue(newField, e.target.value != '' ? [e.target.value] : null)
        }
        const value = watch(newField)

        useEffect(() => {
            register(newField, { disabled })
        }, [register])

        return <select className="select w-full" onChange={onChange} value={value?.[0]} disabled={disabled} {...rest}>
            <option value="">{translate("page.calendar.modal.repeatOptions.no-repeat")}</option>
            <option value="RRULE:FREQ=DAILY">{translate("page.calendar.modal.repeatOptions.daily")}</option>
            <option value="RRULE:FREQ=WEEKLY">{translate("page.calendar.modal.repeatOptions.weekly")}</option>
            <option value="RRULE:FREQ=MONTHLY">{translate("page.calendar.modal.repeatOptions.monthly")}</option>
        </select>
    }

    return <div className={`p-3 prose pt-8 ${className ?? ''}`} {...rest}>
        <h2 className='uppercase'>{translate("page.calendar.modal.title")}</h2>
        <form className="form" onSubmit={handleSubmit(onFinishHandler)}>
            <div className="flex flex-col gap-2">
                <fieldset className="fieldset">
                    <legend>{translate("page.calendar.modal.title-label")} <span className="text-info">{recurringEventId ? `(${translate("page.calendar.modal.recurrent-event")})` : ""}</span></legend>
                    <div className="join">
                        <input type="text" placeholder="Type here" {...register("summary")} readOnly={true} className="join-item input input-bordered input-primary w-full" />
                        {form.refineCore.id && <button type="button" onClick={onDeleteHandler} className="join-item btn"><FaTrash /></button>}
                    </div>

                    <legend>{translate("page.calendar.modal.from")}</legend>
                    <div className="flex flex-wrap">
                        <DateTimeComponent
                            field="start"
                            form={form}
                            disableTime={allDay}
                        />
                    </div>

                    <legend>{translate("page.calendar.modal.to")}</legend>
                    <div className="flex flex-wrap">
                        <DateTimeComponent
                            field="end"
                            form={form}
                            disableTime={allDay}
                        />
                    </div>

                {/* <div className="form-control">
                    <label className="label cursor-pointer">
                        <input type="checkbox" defaultChecked className="checkbox" {...register("all-day")} />
                        <span className="label-text">Todo el dia</span>
                    </label>
                </div> */}

                    <legend>{translate("page.calendar.modal.repeat")} {" "}
                        {recurringEventId ?
                            <a className="link" onClick={() => form.modal.show(recurringEventId)}>{translate("page.calendar.modal.see-primary-event")}</a> : <></>}
                    </legend>
                    <RecurrenceComponent field="recurrence" form={form} disabled={!!recurringEventId} />
                </fieldset>

                <button className="btn btn-primary" type="submit" disabled={formLoading}> {formLoading ? "Loading" : "Save"}</button>
                <CanAccess resource="group-event" action="create">
                    <a className="link link-secondary flex items-center" onClick={() => {
                        go({ to: '/calendar/group-events' })
                        onClose()
                    }}>
                        {translate("page.calendar.modal.create-group-event")} <FaLink className="ml-2" />
                    </a>
                </CanAccess>
            </div>
        </form>
    </div >
}